import { useLayoutEffect, useRef } from 'react';
import {
  type AgendaEvent, type Task, WEEKDAYS_SHORT, addDays, calendarOf, capitalize, fmtDate, fmtShort, fromMinutes, localToday,
  parseISODate, startOfWeek, toISODate, toMinutes,
} from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { useTasks } from '../../data/queries';
import { Page } from '../../layout/Page';
import { openSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { motionOk, EASE } from '../board/effects';
import { openAgendaEvent, useAgendaEvents } from './agendaEvents';

const H_START = 7, H_END = 21, PX = 48;

export function AgendaPage() {
  const ws = useWorkspace();
  const { agendaMode: mode, refDate, set } = useUI();
  const contentRef = useRef<HTMLDivElement>(null);
  const step = (n: number) => {
    const r = parseISODate(refDate);
    set({ refDate: mode === 'month' ? toISODate(new Date(r.getFullYear(), r.getMonth() + n, 1)) : addDays(refDate, 7 * n) });
    if (motionOk()) requestAnimationFrame(() => contentRef.current?.querySelector('.content > *')?.animate([{ opacity: 0.2, transform: `translateX(${n * 28}px)` }, { opacity: 1, transform: 'none' }], { duration: 320, easing: EASE }));
  };
  const ws0 = startOfWeek(refDate);
  return (
    <div style={{ display: 'contents' }} ref={contentRef}>
      <Page toolbar={<>
        <div className="tb-title"><h1>{capitalize(fmtDate(refDate, { month: 'long', year: 'numeric' }))}</h1><span className="sub">{mode === 'week' ? `${fmtShort(ws0)} a ${fmtShort(addDays(ws0, 6))}` : ''}</span></div>
        <div className="tb-space" />
        <div className="tools">
          <div className="seg" role="group" aria-label="Visualização">
            <button aria-pressed={mode === 'week'} onClick={() => set({ agendaMode: 'week' })}>Semana</button>
            <button aria-pressed={mode === 'month'} onClick={() => set({ agendaMode: 'month' })}>Mês</button>
          </div>
          <div className="ctl-group">
            <button aria-label="Anterior" onClick={() => step(-1)}><Icon name="chevL" /></button>
            <button onClick={() => set({ refDate: localToday(), selectedDay: localToday() })}>Hoje</button>
            <button aria-label="Próximo" onClick={() => step(1)}><Icon name="chevR" /></button>
          </div>
          {ws.canWrite && <button className="tb-btn" aria-label="Novo evento" title="Novo evento" onClick={() => openSheet({ kind: 'event', id: null, preset: { date: useUI.getState().selectedDay } })}><Icon name="plus" /></button>}
        </div>
      </>}>
        {mode === 'month' ? <MonthView /> : <WeekView />}
      </Page>
    </div>
  );
}

function useAgendaData(from: string, to: string) {
  const events = useAgendaEvents(from, to);
  const tasks = (useTasks().data ?? []).filter(t => t.due);
  return { events, tasks };
}

/** Classes do item: compromisso do Outlook (externo) e "disponível" ficam visualmente diferentes. */
const evClass = (base: string, e: AgendaEvent) => [base, e.external && 'ext', e.free && 'free'].filter(Boolean).join(' ');
const evTitle = (e: AgendaEvent) => (e.external ? `${e.allDay ? 'Dia inteiro' : `${e.start}–${e.end}`} ${e.title} · abrir no Outlook` : undefined);

function TaskChip({ t }: { t: Task }) {
  const ws = useWorkspace();
  const late = t.status !== 'done' && !!t.due && t.due < localToday();
  const color = ws.settings.projects.find(p => p.id === t.projectId)?.color ?? 'var(--gray)';
  return (
    <button className={`mev ${t.status === 'done' ? 'done' : ''} ${late ? 'late' : ''}`} style={{ '--c': color } as React.CSSProperties} onClick={() => openSheet({ kind: 'task', id: t.id })}>
      <i className="ring" /><span className="t">{t.title}</span>
    </button>
  );
}

function MonthView() {
  const ws = useWorkspace();
  const { refDate, selectedDay, set } = useUI();
  const r = parseISODate(refDate), y = r.getFullYear(), m = r.getMonth();
  const first = new Date(y, m, 1), start = toISODate(new Date(y, m, 1 - first.getDay()));
  const weeks = Math.ceil((first.getDay() + new Date(y, m + 1, 0).getDate()) / 7);
  const { events, tasks } = useAgendaData(start, addDays(start, weeks * 7 - 1));
  const today = localToday();
  return (
    <div className="month" style={{ gridTemplateRows: `auto repeat(${weeks},minmax(104px,1fr))` }}>
      {WEEKDAYS_SHORT.map(d => <div className="wkd" key={d}>{d}</div>)}
      {[...Array(weeks * 7)].map((_, i) => {
        const s = addDays(start, i), d = parseISODate(s);
        const items = [
          ...events.filter(e => e.date === s).sort((a, b) => a.start.localeCompare(b.start)).map(e => (
            <button key={e.id} className={evClass('mev', e)} title={evTitle(e)} style={{ '--c': calendarOf(e.calendar).color } as React.CSSProperties} onClick={() => openAgendaEvent(e)}>
              <i className="dot" /><span className="t">{e.title}</span>{!e.allDay && <span className="h">{e.start}</span>}
            </button>
          )),
          ...tasks.filter(t => t.due === s).sort((a, b) => Number(a.status === 'done') - Number(b.status === 'done')).map(t => <TaskChip key={t.id} t={t} />),
        ];
        const vis = items.length > 4 ? items.slice(0, 3) : items;
        const cls = ['mcell', d.getMonth() !== m && 'other', s === today && 'today', s === selectedDay && 'sel'].filter(Boolean).join(' ');
        return (
          <div key={s} className={cls} onClick={e => { if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.mnum')) set({ selectedDay: s }); }}
            onDoubleClick={e => { if (ws.canWrite && !(e.target as HTMLElement).closest('.mev,.mais')) openSheet({ kind: 'event', id: null, preset: { date: s } }); }}>
            <div className="mnum"><span>{d.getDate() === 1 ? fmtShort(s) : d.getDate()}</span></div>
            {vis}
            {items.length > vis.length && <button className="mais" onClick={() => set({ refDate: s, selectedDay: s, agendaMode: 'week' })}>mais {items.length - vis.length}</button>}
          </div>
        );
      })}
    </div>
  );
}

function lanes(list: AgendaEvent[]) {
  const ends: number[] = [], pos = new Map<string, number>();
  list.forEach(e => {
    let f = ends.findIndex(end => end <= toMinutes(e.start));
    if (f < 0) { f = ends.length; ends.push(0); }
    ends[f] = toMinutes(e.end);
    pos.set(e.id, f);
  });
  return { pos, n: Math.max(1, ends.length) };
}

function WeekView() {
  const ws = useWorkspace();
  const refDate = useUI(s => s.refDate);
  const start = startOfWeek(refDate), days = [...Array(7)].map((_, i) => addDays(start, i)), today = localToday();
  const { events: all, tasks } = useAgendaData(start, days[6]);
  const events = all.filter(e => !e.allDay), allDay = all.filter(e => e.allDay);
  const height = (H_END - H_START) * PX;
  const now = new Date(), mNow = now.getHours() * 60 + now.getMinutes();
  const scrolled = useRef('');
  const root = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const c = root.current?.closest('.content') as HTMLElement | null;
    if (!c || scrolled.current === start) return;
    c.scrollTop = Math.max(0, ((days.includes(today) ? now.getHours() - 1.5 : 8.5) - H_START) * PX);
    scrolled.current = start;
  });

  return (
    <div className="week" ref={root}>
      <div className="wk-head"><div />{days.map(s => { const d = parseISODate(s); return <div key={s} className={`wh ${s === today ? 'today' : ''}`}><span className="wd">{WEEKDAYS_SHORT[d.getDay()]}</span><span className="wn">{d.getDate()}</span></div>; })}</div>
      <div className="wk-allday"><div className="lab">dia todo</div>{days.map(s => (
        <div key={s} className="cell">
          {allDay.filter(e => e.date === s).map(e => (
            <button key={e.id} className={evClass('mev', e)} title={evTitle(e)} style={{ '--c': calendarOf(e.calendar).color } as React.CSSProperties} onClick={() => openAgendaEvent(e)}>
              <i className="dot" /><span className="t">{e.title}</span>
            </button>
          ))}
          {tasks.filter(t => t.due === s).map(t => <TaskChip key={t.id} t={t} />)}
        </div>
      ))}</div>
      <div className="wk-body">
        <div className="gutter" style={{ height }}>{[...Array(H_END - H_START)].map((_, i) => i ? <span key={i} style={{ top: i * PX }}>{String(H_START + i).padStart(2, '0')}:00</span> : null)}</div>
        {days.map(s => {
          const list = events.filter(e => e.date === s).sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
          const { pos, n } = lanes(list), w = 100 / n;
          return (
            <div key={s} className={`wcol ${s === today ? 'today' : ''}`} style={{ height, '--px': `${PX}px` } as React.CSSProperties}
              onDoubleClick={e => {
                if (!ws.canWrite || (e.target as HTMLElement).closest('.wev')) return;
                const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
                const m = Math.floor((H_START * 60 + (y / PX) * 60) / 30) * 30;
                openSheet({ kind: 'event', id: null, preset: { date: s, start: fromMinutes(m), end: fromMinutes(Math.min(23 * 60 + 30, m + 60)) } });
              }}>
              {list.map(e => {
                const top = Math.max(0, ((toMinutes(e.start) - H_START * 60) / 60) * PX), bot = Math.min(height, ((toMinutes(e.end) - H_START * 60) / 60) * PX), h = Math.max(20, bot - top - 2);
                return (
                  <button key={e.id} className={evClass('wev', e)} title={evTitle(e)} onClick={() => openAgendaEvent(e)}
                    style={{ '--c': calendarOf(e.calendar).color, top: top + 1, height: h, left: `calc(${pos.get(e.id)! * w}% + 2px)`, width: `calc(${w}% - 5px)` } as React.CSSProperties}>
                    <span className="t">{e.title}</span>{h > 34 && <span className="h">{e.start}–{e.end}{e.location ? ', ' + e.location : ''}</span>}
                  </button>
                );
              })}
              {s === today && mNow >= H_START * 60 && mNow <= H_END * 60 && <div className="now" style={{ top: ((mNow - H_START * 60) / 60) * PX }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

